import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ApiError } from "@/api/problem";
import { UpgradeLink } from "@/features/billing/UpgradeLink";
import { Button, Field, Input, Notice, Select } from "@/components/ui/Field";
import { meQuery } from "@/features/auth/session";
import { SettingsHeader } from "@/features/settings/SettingsHeader";
import { useUpdateWorkspace, workspaceQuery } from "@/features/settings/queries";
import { timeZoneLabel, timeZoneOptions } from "@/features/settings/timezones";

const schema = z.object({
  name: z.string().trim().min(1, "settings.errors.nameRequired").max(120),
  timezone: z.string().min(1),
});
type Values = z.infer<typeof schema>;

export function WorkspaceSettingsPage() {
  const { t } = useTranslation();
  const workspace = useQuery(workspaceQuery);
  const update = useUpdateWorkspace();
  const [saved, setSaved] = useState(false);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", timezone: "" },
  });

  // The form is populated once the workspace arrives. `reset` rather than `setValue` so the
  // fields count as pristine, and a save that changed nothing does not look like an edit.
  const { reset } = form;
  useEffect(() => {
    if (workspace.data) {
      reset({ name: workspace.data.name, timezone: workspace.data.timezone });
    }
  }, [workspace.data, reset]);

  const submit = form.handleSubmit(async (values) => {
    setSaved(false);
    try {
      await update.mutateAsync(values);
      setSaved(true);
      reset(values);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : t("settings.errors.saveFailed");
      form.setError("root", { message });
    }
  });

  if (workspace.isPending) return <p className="text-stone-500">{t("common.loading")}</p>;
  if (workspace.isError) return <Notice tone="error">{t("settings.errors.loadFailed")}</Notice>;

  const zones = timeZoneOptions(workspace.data?.timezone);

  return (
    <section className="space-y-5">
      <SettingsHeader
        title={t("settings.workspace.title")}
        description={t("settings.workspace.subtitle")}
      />

      <form onSubmit={submit} noValidate className="space-y-4">
        <Field
          name="name"
          label={t("settings.workspace.name")}
          error={
            form.formState.errors.name?.message ? t(form.formState.errors.name.message) : undefined
          }
        >
          <Input id="name" invalid={!!form.formState.errors.name} {...form.register("name")} />
        </Field>

        <Field name="timezone" label={t("settings.workspace.timezone")}>
          <Select id="timezone" {...form.register("timezone")}>
            {zones.map((zone) => (
              <option key={zone} value={zone}>
                {timeZoneLabel(zone)}
              </option>
            ))}
          </Select>
        </Field>

        <p className="text-sm text-stone-500">
          {t("settings.workspace.currency", { currency: workspace.data?.default_currency })}
        </p>

        {form.formState.errors.root && (
          <Notice tone="error">{form.formState.errors.root.message}</Notice>
        )}
        {saved && !form.formState.isDirty && <Notice tone="success">{t("common.saved")}</Notice>}

        <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
          {update.isPending ? t("common.saving") : t("common.save")}
        </Button>
      </form>

      <ReceiptPhotos keep={workspace.data.keep_receipt_images} />
    </section>
  );
}

/**
 * Whether receipt photos stay after confirming. A plan with image retention lets the owner
 * choose; without one each photo is deleted, and the paper is what the CRA can ask to see.
 * Saved as soon as it changes, apart from the form above: it is a switch, not an edit.
 */
function ReceiptPhotos({ keep }: { keep: boolean }) {
  const { t } = useTranslation();
  const me = useQuery(meQuery);
  const update = useUpdateWorkspace();
  const [error, setError] = useState<string | undefined>();
  const offered = me.data?.workspace?.image_retention ?? false;
  const readOnly = me.data?.workspace?.read_only ?? false;

  async function change(next: boolean) {
    setError(undefined);
    try {
      await update.mutateAsync({ keep_receipt_images: next });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("settings.errors.saveFailed"));
    }
  }

  return (
    <section aria-labelledby="photos-title" className="space-y-2 border-t border-stone-200 pt-5">
      <h2 id="photos-title" className="font-medium text-stone-800">
        {t("settings.workspace.photos.title")}
      </h2>
      {readOnly ? (
        <p className="text-sm text-stone-600">{t("settings.workspace.photos.readOnly")}</p>
      ) : offered ? (
        <>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-0.5 h-5 w-5 shrink-0"
              checked={keep}
              disabled={update.isPending}
              onChange={(event) => void change(event.target.checked)}
            />
            <span className="text-stone-800">{t("settings.workspace.photos.keep")}</span>
          </label>
          <p className="text-sm text-stone-600">
            {keep ? t("settings.workspace.photos.kept") : t("settings.workspace.photos.deleted")}
          </p>
        </>
      ) : (
        <p className="text-sm text-stone-600">
          {t("settings.workspace.photos.notOnPlan", { plan: me.data?.workspace?.plan_name ?? "" })}{" "}
          <UpgradeLink />
        </p>
      )}
      {error && <Notice tone="error">{error}</Notice>}
    </section>
  );
}
